import { useMemo } from 'react'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { MoreVertIcon, TabDuplicateIcon, ModeOffOnIcon, DeleteIcon } from '@/components/icons'
import { Link2, Play } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useRoles } from '@/hooks'
import { parseChainYaml } from '@/lib/chainYaml'
import { CHAIN_CATEGORY_DOT_COLORS } from '@/types'
import type { ChainDefinitionSummary } from '@/types'

interface ChainsListTableProps {
  chains: ChainDefinitionSummary[]
  canStart: boolean
  canDuplicate: boolean
  canToggle: boolean
  canDelete: boolean
  onNavigate: (chain: ChainDefinitionSummary) => void
  onStart: (chain: ChainDefinitionSummary) => void
  onDuplicate: (chain: ChainDefinitionSummary) => void
  onToggleActive: (chain: ChainDefinitionSummary) => void
  onDelete: (chain: ChainDefinitionSummary) => void
}

interface ListRowProps extends Omit<ChainsListTableProps, 'chains'> {
  chain: ChainDefinitionSummary
  roles: { id: string; name: string }[] | undefined
}

function ListRow({ chain, roles, canStart, canDuplicate, canToggle, canDelete, onNavigate, onStart, onDuplicate, onToggleActive, onDelete }: ListRowProps) {
  const linkCount = useMemo(() => {
    try {
      return parseChainYaml(chain.yamlContent).links.length
    } catch {
      return 0
    }
  }, [chain.yamlContent])

  const roleLabel = useMemo(() => {
    if (!roles || chain.permittedRoleIds.length === 0) return null
    const nameById = new Map(roles.map((r) => [r.id, r.name]))
    const names = chain.permittedRoleIds.map((id) => nameById.get(id)).filter((n): n is string => !!n)
    if (names.length === 0) return null
    if (names.length <= 2) return names.join(', ')
    return `${names[0]}, ${names[1]} +${names.length - 2}`
  }, [roles, chain.permittedRoleIds])

  return (
    <tr
      className="group border-b border-border hover:bg-muted cursor-pointer transition-colors has-[[data-state=open]]:bg-muted"
      data-testid={`chain-row-${chain.id}`}
      role="button"
      tabIndex={0}
      onClick={() => onNavigate(chain)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onNavigate(chain)
        }
      }}
    >
      <td className="px-4 py-3 break-words">
        <span className="text-sm font-bold leading-[1.6] tracking-[0.015em] text-card-foreground">
          {chain.displayName}
        </span>
        <Badge variant="outline" className="ml-2 text-xs" style={{ borderColor: 'rgba(118,124,150,1)' }}>
          V{chain.version}
        </Badge>
      </td>

      <td className="px-4 py-3">
        <Badge variant="outline" className="text-xs" style={{ borderColor: 'rgba(118,124,150,1)' }}>
          {chain.isSystem ? 'System' : 'Custom'}
        </Badge>
      </td>

      <td className="px-4 py-3">
        <span className="flex items-center gap-2 text-sm text-card-foreground break-words">
          <span className={cn('h-2 w-2 rounded-full shrink-0', CHAIN_CATEGORY_DOT_COLORS[chain.category ?? 'Uncategorized'])} />
          {chain.category ?? 'Uncategorized'}
        </span>
      </td>

      <td className="px-4 py-3">
        <Badge
          variant="outline"
          className={cn(
            'text-xs font-bold leading-[1.6] tracking-[0.015em] rounded-full px-2.5',
            chain.isActive
              ? 'h-6 bg-active-badge-bg text-active-badge-fg hover:bg-active-badge-bg'
              : 'h-6 bg-inactive-badge-bg text-inactive-badge-fg hover:bg-inactive-badge-bg',
          )}
          style={{ borderColor: chain.isActive ? 'rgba(38,132,255,1)' : 'rgba(128,133,155,1)' }}
        >
          {chain.isActive ? 'Active' : 'Inactive'}
        </Badge>
      </td>

      <td className="px-4 py-3 text-sm text-card-foreground">
        <span className="flex items-center gap-1.5">
          <Link2 className="h-4 w-4" />
          {linkCount}
        </span>
      </td>

      <td className="px-4 py-3 text-sm text-card-foreground break-words">
        {roleLabel ?? '—'}
      </td>

      <td className="px-4 py-3 max-w-[240px]">
        {chain.description ? (
          <p className="text-sm text-card-foreground truncate" title={chain.description}>{chain.description}</p>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        )}
      </td>

      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-end">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="shrink-0 text-foreground focus:outline-none p-1.5 rounded-lg hover:bg-[rgba(224,229,229,0.5)] data-[state=open]:bg-[rgba(224,229,229,0.5)]"
                aria-label={`More options for ${chain.displayName}`}
                data-testid={`chain-menu-trigger-${chain.id}`}
              >
                <MoreVertIcon className="h-5 w-5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              side="bottom"
              align="end"
              sideOffset={4}
              onClick={(e) => e.stopPropagation()}
              className="border-none shadow-elevated rounded-lg py-2 px-0 bg-background gap-0.5 min-w-0"
            >
              {canStart && chain.isActive && (
                <DropdownMenuItem
                  onClick={() => onStart(chain)}
                  className="h-10 px-4 text-foreground focus:text-foreground cursor-pointer hover:bg-muted font-bold leading-[1.6] tracking-[0.015em] [&_svg]:size-5"
                  data-testid={`chain-start-${chain.id}`}
                >
                  <Play className="h-5 w-5" />
                  Start
                </DropdownMenuItem>
              )}
              {canStart && chain.isActive && canDuplicate && <DropdownMenuSeparator />}
              {canDuplicate && (
                <DropdownMenuItem
                  onClick={() => onDuplicate(chain)}
                  className="h-10 px-4 text-foreground focus:text-foreground cursor-pointer hover:bg-muted font-bold leading-[1.6] tracking-[0.015em] [&_svg]:size-5"
                >
                  <TabDuplicateIcon className="h-5 w-5" />
                  Duplicate
                </DropdownMenuItem>
              )}
              {canDelete && !chain.isSystem && !chain.isActive && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => onDelete(chain)}
                    className="h-10 px-4 text-foreground focus:text-foreground cursor-pointer hover:bg-muted font-bold leading-[1.6] tracking-[0.015em] [&_svg]:size-5"
                  >
                    <DeleteIcon className="h-5 w-5" />
                    Delete
                  </DropdownMenuItem>
                </>
              )}
              {canToggle && !chain.isSystem && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => onToggleActive(chain)}
                    className="h-10 px-4 text-foreground focus:text-foreground cursor-pointer hover:bg-muted font-bold leading-[1.6] tracking-[0.015em] [&_svg]:size-5"
                  >
                    <ModeOffOnIcon className="h-5 w-5" />
                    {chain.isActive ? 'Deactivate' : 'Activate'}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </td>
    </tr>
  )
}

export function ChainsListTable({ chains, ...rowProps }: ChainsListTableProps) {
  const { data: roles } = useRoles()

  return (
    <div className="overflow-x-auto rounded-lg border border-border" data-testid="chains-list-table">
      <table className="w-full text-left" aria-label="Chains">
        <thead>
          <tr className="border-b border-border bg-table-header">
            <th className="px-4 py-3 text-xs font-bold uppercase tracking-[0.08em] text-foreground">Chain Name</th>
            <th className="px-4 py-3 text-xs font-bold uppercase tracking-[0.08em] text-foreground">Type</th>
            <th className="px-4 py-3 text-xs font-bold uppercase tracking-[0.08em] text-foreground">Category</th>
            <th className="px-4 py-3 text-xs font-bold uppercase tracking-[0.08em] text-foreground">Status</th>
            <th className="px-4 py-3 text-xs font-bold uppercase tracking-[0.08em] text-foreground">Links</th>
            <th className="px-4 py-3 text-xs font-bold uppercase tracking-[0.08em] text-foreground">Available To</th>
            <th className="px-4 py-3 text-xs font-bold uppercase tracking-[0.08em] text-foreground">Description</th>
            <th className="px-4 py-3 text-xs font-bold uppercase tracking-[0.08em] text-foreground text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {chains.map((chain) => (
            <ListRow key={chain.id} chain={chain} roles={roles} {...rowProps} />
          ))}
        </tbody>
      </table>
    </div>
  )
}
